/**
 * Invoice Template Designer Page (v4)
 * Grid-based container system with modern UI
 */

import { useState, useEffect, useRef } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Save, Grid3x3, Ruler, Settings, Download, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useToast } from '@/hooks/use-toast'
import { useDocumentTitle } from '@/hooks/use-document-title'
import {
  useUpdateInvoiceTemplate,
  useTemplateById,
} from '@/features/invoice-template/hooks/use-template'
import { GridCanvas } from '@/features/invoice-template/components/canvas/grid-canvas'
import { GenericPropertyGrid, type CellData } from '@/features/invoice-template/components/properties/generic-property-grid'
import { OutlineView } from '@/features/invoice-template/components/outline/outline-view'
import type { ContainerElement, AbsoluteElement, TemplateElement, InvoiceTemplate } from '@/features/invoice-template/types/template.types'
import { useTranslation } from 'react-i18next'
import { createAbsoluteElementHandlers } from '@/features/invoice-template/handlers/absolute-element-handlers'
import { createSaveHandler } from '@/features/invoice-template/handlers/template-save-handler'
import { createElementUpdateHandlers } from '@/features/invoice-template/handlers/element-update-handlers'
import { exportTemplate, importTemplate, uploadImagesAndUpdateTemplate } from '@/features/invoice-template/utils/template-import-export'
import {
  getSelectedContainer,
  getSelectedElement,
  getParentContainerMaxHeight,
} from '@/features/invoice-template/utils/element-finder'
import { FeatureGate } from '@/components/ui/feature-gate'

export const Route = createFileRoute('/(app)/invoice-template/')({
  component: InvoiceTemplateDesigner,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      id: (search.id as string) || undefined,
    }
  },
})

function InvoiceTemplateDesigner() {
  const navigate = useNavigate()
  
  useDocumentTitle('invoiceTemplate.title')
  
  return (
    <FeatureGate
      featureId="template-designer"
      fallback={
        <div className="container mx-auto py-6 space-y-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate({ to: "/dashboard/invoice-template" })}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Template Designer</h1>
              <p className="text-muted-foreground">
                Erweiterte Template-Design-Funktionen
              </p>
            </div>
          </div>
          
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center">
              <Settings className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-xl font-semibold">Pro-Feature</h2>
            <p className="text-muted-foreground text-center max-w-md">
              Der Template Designer ist nur für Pro-Nutzer verfügbar. 
              Upgraden Sie auf Pro, um benutzerdefinierte Rechnungsvorlagen zu erstellen.
            </p>
            <Button onClick={() => navigate({ to: "/dashboard/settings" })}>
              Upgrade auf Pro
            </Button>
          </div>
        </div>
      }
    >
      <TemplateDesignerContent />
    </FeatureGate>
  )
}

function TemplateDesignerContent() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { t } = useTranslation()
  const search = Route.useSearch()
  const templateId = search.id
  const isNewTemplate = !templateId || templateId === 'new'
  
  useDocumentTitle('invoiceTemplate.title')
  
  // Canvas ref for screenshot capture
  const canvasRef = useRef<HTMLDivElement>(null!)
  
  // Only fetch template if we have a valid ID (not "new")
  const { data: template, isLoading } = useTemplateById(templateId!, {
    enabled: !isNewTemplate,
  })
  const updateTemplate = useUpdateInvoiceTemplate()

  // Template metadata
  const [templateName, setTemplateName] = useState('Neues Template')

  // Global settings
  const [globalFontFamily, setGlobalFontFamily] = useState('Helvetica')
  const [globalFontSize, setGlobalFontSize] = useState(10)
  const [globalFontColor, setGlobalFontColor] = useState('#000000')

  // Grid settings
  const [showGrid, setShowGrid] = useState(true)
  const [gridSize, setGridSize] = useState(9.45)
  const [showRulers, setShowRulers] = useState(true)

  // Margin settings (stored in pixels, displayed in cm)
  const CM_TO_PX = 96 / 2.54 // ≈ 37.795
  const [leftMargin, setLeftMargin] = useState(56.7)
  const [rightMargin, setRightMargin] = useState(56.7)
  const [topMargin, setTopMargin] = useState(56.7)
  const [bottomMargin, setBottomMargin] = useState(56.7)
  const [showMargins, setShowMargins] = useState(true)
  const [showEnvelopeWindow, setShowEnvelopeWindow] = useState<'none' | 'form-a' | 'form-b'>('none')

  // Root container state - start with empty array for container list
  const [rootContainer, setRootContainer] = useState<ContainerElement | ContainerElement[] | null>(
    null,
  )
  
  // Header and Footer containers - always present
  const [headerContainer, setHeaderContainer] = useState<ContainerElement>({
    id: 'header-container',
    type: 'container',
    visible: true,
    rows: 1,
    columns: 3,
    height: 100,
    width: 100,
    horizontalAlign: 'stretch',
    verticalAlign: 'stretch',
    children: [],
  })
  
  const [footerContainer, setFooterContainer] = useState<ContainerElement>({
    id: 'footer-container',
    type: 'container',
    visible: true,
    rows: 1,
    columns: 3,
    height: 80,
    width: 100,
    horizontalAlign: 'stretch',
    verticalAlign: 'stretch',
    children: [],
  })

  // Absolutely positioned elements
  const [absoluteElements, setAbsoluteElements] = useState<AbsoluteElement[]>([])
  
  // Clipboard for absolute elements
  const [clipboard, setClipboard] = useState<AbsoluteElement | null>(null)

  // Selection
  const [selectedElementId, setSelectedElementId] = useState<string | null>(
    null,
  )

  // Track if there are unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Manual row height input (per selected row)
  const [rowHeightInput, setRowHeightInput] = useState('')

  // Create absolute element handlers
  const absoluteHandlers = createAbsoluteElementHandlers(
    absoluteElements,
    setAbsoluteElements,
    clipboard,
    setClipboard,
    setSelectedElementId
  )

  // Create element update handlers
  const updateHandlers = createElementUpdateHandlers({
    headerContainer,
    footerContainer,
    rootContainer,
    absoluteElements,
    setHeaderContainer,
    setFooterContainer,
    setRootContainer,
    setAbsoluteElements,
    setHasUnsavedChanges,
  })

  // Handle element deletion
  const handleDeleteElement = (elementId: string) => {
    console.log('Deleting element:', elementId);
    
    // Helper to check if element exists in container
    const elementExistsInContainer = (container: ContainerElement, id: string): boolean => {
      if (container.id === id) return true;
      const children = Array.isArray(container.children) ? container.children : [];
      for (const child of children) {
        if (child.element.id === id) return true;
        if (child.element.type === 'container') {
          if (elementExistsInContainer(child.element as ContainerElement, id)) return true;
        }
      }
      return false;
    };
    
    // Helper to remove element from container recursively
    const removeElementFromContainer = (container: ContainerElement): ContainerElement => {
      const children = Array.isArray(container.children) ? container.children : [];
      return {
        ...container,
        children: children
          .filter(child => child.element.id !== elementId)
          .map(child => ({
            ...child,
            element: child.element.type === 'container'
              ? removeElementFromContainer(child.element as ContainerElement)
              : child.element
          }))
      };
    };

    // Check if it's an absolute element
    const isAbsoluteElement = absoluteElements.some(el => el.id === elementId);
    if (isAbsoluteElement) {
      console.log('Deleting absolute element');
      setAbsoluteElements(absoluteElements.filter(el => el.id !== elementId));
      setSelectedElementId('');
      return;
    }

    // Check if it's in the header container
    if (elementExistsInContainer(headerContainer, elementId)) {
      console.log('Deleting from header');
      const updatedHeader = removeElementFromContainer(headerContainer);
      setHeaderContainer(updatedHeader);
      setSelectedElementId('');
      return;
    }

    // Check if it's in the footer container
    if (elementExistsInContainer(footerContainer, elementId)) {
      console.log('Deleting from footer');
      const updatedFooter = removeElementFromContainer(footerContainer);
      setFooterContainer(updatedFooter);
      setSelectedElementId('');
      return;
    }

    // Check if it's in root containers
    if (rootContainer) {
      if (Array.isArray(rootContainer)) {
        for (const container of rootContainer) {
          if (elementExistsInContainer(container, elementId)) {
            console.log('Deleting from root array');
            const updatedRoot = rootContainer.map(c => removeElementFromContainer(c));
            setRootContainer(updatedRoot);
            setSelectedElementId('');
            return;
          }
        }
      } else {
        if (elementExistsInContainer(rootContainer, elementId)) {
          console.log('Deleting from single root');
          const updatedRoot = removeElementFromContainer(rootContainer);
          setRootContainer(updatedRoot);
          setSelectedElementId('');
          return;
        }
      }
    }
    
    console.log('Element not found:', elementId);
  }

  // Handle row deletion from containers
  const handleDeleteRow = (containerId: string, rowIndex: number) => {
    console.log('Deleting row:', rowIndex, 'from container:', containerId);
    
    // Helper to remove row from container recursively
    const removeRowFromContainer = (container: ContainerElement): ContainerElement => {
      if (container.id !== containerId) {
        // Not the target container, but might be a nested container
        return {
          ...container,
          children: container.children?.map(child => ({
            ...child,
            element: child.element.type === 'container'
              ? removeRowFromContainer(child.element as ContainerElement)
              : child.element
          }))
        };
      }

      // This is the target container - remove the row
      const newRows = container.rows - 1;
      const childrenToKeep = container.children.filter(child => child.cellRow !== rowIndex);
      
      // Adjust row indices for elements in rows after the deleted row
      const adjustedChildren = childrenToKeep.map(child => ({
        ...child,
        cellRow: child.cellRow > rowIndex ? child.cellRow - 1 : child.cellRow
      }));

      return {
        ...container,
        rows: newRows,
        children: adjustedChildren
      };
    };

    // Update containers
    if (headerContainer.id === containerId) {
      const updatedHeader = removeRowFromContainer(headerContainer);
      setHeaderContainer(updatedHeader);
    } else if (footerContainer.id === containerId) {
      const updatedFooter = removeRowFromContainer(footerContainer);
      setFooterContainer(updatedFooter);
    } else {
      // Check root containers
      if (rootContainer) {
        if (Array.isArray(rootContainer)) {
          const updatedRoot = rootContainer.map(c => removeRowFromContainer(c));
          setRootContainer(updatedRoot);
        } else if (rootContainer.id === containerId) {
          const updatedRoot = removeRowFromContainer(rootContainer);
          setRootContainer(updatedRoot);
        }
      }
    }

    setSelectedElementId('');
  }

  // Create save handler
  const handleSave = createSaveHandler(
    {
      templateId,
      isNewTemplate,
      templateName,
      globalFontFamily,
      globalFontSize,
      globalFontColor,
      headerContainer,
      footerContainer,
      rootContainer,
      absoluteElements,
      canvasRef,
      setHasUnsavedChanges,
      leftMargin,
      rightMargin,
      topMargin,
      bottomMargin,
    },
    {
      onSuccess: () => {
        toast({
          title: 'Saved',
          description: 'Template saved successfully',
        })
        navigate({ to: '/dashboard/settings/invoice-templates' })
      },
      onError: () => {
        toast({
          title: 'Error',
          description: 'Failed to save template',
          variant: 'destructive',
        })
      },
      onValidationError: (message) => {
        toast({
          title: 'Error',
          description: message,
          variant: 'destructive',
        })
      },
    }
  )

  // Handle template export
  const handleExport = async () => {
    if (!template) {
      toast({
        title: 'Fehler',
        description: 'Kein Template zum Exportieren vorhanden',
        variant: 'destructive',
      })
      return
    }

    try {
      // Create complete template with current state values including margins
      const exportTemplate_data: InvoiceTemplate = {
        ...template,
        rootContainer: {
          ...template.rootContainer,
          containers: rootContainer || [],
          header: headerContainer || undefined,
          footer: footerContainer || undefined,
          leftMargin,
          rightMargin,
          topMargin,
          bottomMargin,
        },
      }
      
      await exportTemplate(exportTemplate_data)
      toast({
        title: 'Export erfolgreich',
        description: 'Template wurde als ZIP-Datei heruntergeladen',
      })
    } catch (error) {
      console.error('Export failed:', error)
      toast({
        title: 'Export fehlgeschlagen',
        description: 'Template konnte nicht exportiert werden',
        variant: 'destructive',
      })
    }
  }

  // Handle template import
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      const { template: importedTemplate, images } = await importTemplate(file)
      
      // Upload images and get new URLs
      const updatedTemplate = await uploadImagesAndUpdateTemplate(importedTemplate, images)
      
      // Apply imported template data
      setTemplateName(updatedTemplate.name || 'Importiertes Template')
      setGlobalFontFamily(updatedTemplate.fontFamily || 'Helvetica')
      setGlobalFontSize(updatedTemplate.fontSize || 10)
      setGlobalFontColor(updatedTemplate.fontColor || '#000000')
      
      // Apply root container with all settings including margins
      if (updatedTemplate.rootContainer) {
        if (updatedTemplate.rootContainer.containers) {
          setRootContainer(updatedTemplate.rootContainer.containers)
        }
        if (updatedTemplate.rootContainer.header) {
          setHeaderContainer(updatedTemplate.rootContainer.header as ContainerElement)
        }
        if (updatedTemplate.rootContainer.footer) {
          setFooterContainer(updatedTemplate.rootContainer.footer as ContainerElement)
        }
        if (updatedTemplate.rootContainer.leftMargin !== undefined) {
          setLeftMargin(updatedTemplate.rootContainer.leftMargin)
        }
        if (updatedTemplate.rootContainer.rightMargin !== undefined) {
          setRightMargin(updatedTemplate.rootContainer.rightMargin)
        }
        if (updatedTemplate.rootContainer.topMargin !== undefined) {
          setTopMargin(updatedTemplate.rootContainer.topMargin)
        }
        if (updatedTemplate.rootContainer.bottomMargin !== undefined) {
          setBottomMargin(updatedTemplate.rootContainer.bottomMargin)
        }
      }
      
      setHasUnsavedChanges(true)
      
      toast({
        title: 'Import erfolgreich',
        description: 'Template wurde importiert',
      })
    } catch (error) {
      console.error('Import failed:', error)
      toast({
        title: 'Import fehlgeschlagen',
        description: error instanceof Error ? error.message : 'Template konnte nicht importiert werden',
        variant: 'destructive',
      })
    }
    
    // Reset file input
    event.target.value = ''
  }

  // Get selected elements using utility functions
  const selectedContainer = getSelectedContainer(
    selectedElementId,
    headerContainer,
    footerContainer,
    rootContainer
  )

  const selectedElement = getSelectedElement(
    selectedElementId,
    headerContainer,
    footerContainer,
    rootContainer,
    absoluteElements
  )

  // Get selected row data (if a row is selected)
  const getSelectedRowData = (): { containerId: string; row: number; rowHeight: string; backgroundColor: string } | null => {
    if (!selectedElementId || !selectedElementId.includes('-row-')) return null;
    
    console.log('Row selection - selectedElementId:', selectedElementId);
    
    const parts = selectedElementId.split('-row-');
    if (parts.length !== 2) return null;
    
    const containerId = parts[0];
    const row = parseInt(parts[1] || '0');
    
    console.log('Row selection - parsed:', { containerId, row });
    
    if (isNaN(row)) return null;
    
    // Find the container
    let container: ContainerElement | null = null;
    
    if (headerContainer.id === containerId) {
      container = headerContainer;
    } else if (footerContainer.id === containerId) {
      container = footerContainer;
    } else {
      // Search in rootContainer (which can be array or single container)
      const findContainer = (c: ContainerElement): ContainerElement | null => {
        if (c.id === containerId) return c;
        if (!c.children || c.children.length === 0) return null;
        for (const child of c.children) {
          if (child.element.type === 'container') {
            const found = findContainer(child.element as ContainerElement);
            if (found) return found;
          }
        }
        return null;
      };
      
      // Check if rootContainer is array or single container
      if (Array.isArray(rootContainer)) {
        for (const rc of rootContainer) {
          const found = findContainer(rc);
          if (found) {
            container = found;
            break;
          }
        }
      } else if (rootContainer) {
        container = findContainer(rootContainer);
      }
      
      // Also search in header and footer if not found
      if (!container) {
        container = findContainer(headerContainer) || findContainer(footerContainer);
      }
    }
    
    console.log('Row selection - found container:', container?.id);
    
    if (!container) return null;
    
    // Get current row height (default to '1fr' if not set)
    const rowHeight = container.rowHeights?.[row] || '1fr';
    const backgroundColor = container.rowBackgroundColors?.[row] || '';
    
    console.log('Row selection - result:', { containerId, row, rowHeight, backgroundColor });
    
    return { containerId: containerId || '', row, rowHeight: rowHeight || '1fr', backgroundColor: backgroundColor || '' };
  };

  const selectedRowInfo = getSelectedRowData();

  useEffect(() => {
    if (!selectedRowInfo) {
      setRowHeightInput('');
      return;
    }

    const { rowHeight } = selectedRowInfo;
    if (rowHeight.endsWith('px')) {
      const numericValue = parseFloat(rowHeight);
      setRowHeightInput(Number.isFinite(numericValue) ? String(numericValue) : '');
    } else {
      setRowHeightInput('');
    }
  }, [selectedRowInfo]);

  const rowHeightPresets = [
    { value: 'auto', label: 'Auto (Inhalt)' },
    { value: '1fr', label: 'Gleich verteilt (1fr)' },
    { value: '2fr', label: 'Doppelt (2fr)' },
    { value: '50px', label: '50px (fest)' },
    { value: '100px', label: '100px (fest)' },
    { value: '150px', label: '150px (fest)' },
    { value: '200px', label: '200px (fest)' },
    { value: 'minmax(50px, auto)', label: 'Min 50px, Auto' },
    { value: 'minmax(100px, auto)', label: 'Min 100px, Auto' },
  ];
  const handleRowHeightInputChange = (value: string) => {
    setRowHeightInput(value);
    if (!selectedRowInfo) return;
    if (value.trim() === '') return;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return;
    const nextHeight = `${numericValue}px`;
    if (nextHeight === selectedRowInfo.rowHeight) return;
    handleRowHeightChange(nextHeight);
  };

  // Get selected cell data (if a cell is selected)
  const getSelectedCellData = (): { cellData: CellData; containerId: string; row: number; col: number } | null => {
    if (!selectedElementId || !selectedElementId.includes('-cell-')) return null;
    
    console.log('Cell selection - selectedElementId:', selectedElementId);
    
    const parts = selectedElementId.split('-cell-');
    if (parts.length !== 2) {
      console.log('Cell selection - invalid parts:', parts);
      return null;
    }
    
    const containerId = parts[0];
    const [rowStr, colStr] = parts[1]?.split('-') || ['', ''];
    const row = parseInt(rowStr || '0');
    const col = parseInt(colStr || '0');
    
    console.log('Cell selection - parsed:', { containerId, row, col });
    
    if (isNaN(row) || isNaN(col)) {
      console.log('Cell selection - invalid row/col');
      return null;
    }
    
    // Find the container and cell
    let container: ContainerElement | null = null;
    console.log('Cell selection - searching for container:', containerId);
    console.log('Cell selection - header:', headerContainer.id);
    console.log('Cell selection - footer:', footerContainer.id);
    console.log('Cell selection - rootContainer type:', Array.isArray(rootContainer) ? 'array' : typeof rootContainer);
    
    // Recursive search function
    const findContainer = (c: ContainerElement): ContainerElement | null => {
      console.log('Cell selection - checking container:', c.id);
      if (c.id === containerId) return c;
      if (!c.children || c.children.length === 0) return null;
      for (const child of c.children) {
        if (child.element.type === 'container') {
          const found = findContainer(child.element as ContainerElement);
          if (found) return found;
        }
      }
      return null;
    };
    
    // Search in header (including nested containers)
    container = findContainer(headerContainer);
    
    // Search in footer (including nested containers)
    if (!container) {
      container = findContainer(footerContainer);
    }
    
    // Search in root containers
    if (!container && rootContainer) {
      if (Array.isArray(rootContainer)) {
        // Search through all containers in the array
        for (const c of rootContainer) {
          container = findContainer(c);
          if (container) break;
        }
      } else {
        // Search single root container
        container = findContainer(rootContainer);
      }
    }
    
    console.log('Cell selection - container found:', container?.id);
    
    if (!container) {
      console.log('Cell selection - container not found!');
      return null;
    }
    
    const cellKey = `${row}-${col}`;
    const child = container.children.find(c => c.cellRow === row && c.cellColumn === col);
    
    // Get alignment from element, backgroundColor from child
    const cellData: CellData = {
      horizontalAlign: child?.element.horizontalAlign || 'left',
      verticalAlign: child?.element.verticalAlign || 'top',
      backgroundColor: child?.backgroundColor || 'transparent',
    };
    
    return { cellData, containerId: containerId || '', row: row || 0, col: col || 0 };
  };

  const selectedCellInfo = getSelectedCellData();

  // Handle cell alignment change
  const handleCellChange = (element: AbsoluteElement | TemplateElement | CellData) => {
    const updatedCell = element as CellData;
    console.log('handleCellChange called with:', updatedCell);
    if (!selectedCellInfo) return;
    
    const { containerId, row, col } = selectedCellInfo;
    const cellKey = `${row}-${col}`;
    console.log('Updating cell:', { containerId, row, col, cellKey });
    
    const updateContainerCell = (container: ContainerElement): ContainerElement => {
      if (container.id === containerId) {
        const child = container.children.find(c => c.cellRow === row && c.cellColumn === col);
        
        if (child) {
          // Update the child element's alignment and cell's backgroundColor
          const updatedChildren = container.children.map(c => {
            if (c.cellRow === row && c.cellColumn === col) {
              return {
                ...c,
                backgroundColor: updatedCell.backgroundColor,
                element: {
                  ...c.element,
                  horizontalAlign: updatedCell.horizontalAlign,
                  verticalAlign: updatedCell.verticalAlign,
                },
              };
            }
            return c;
          });
          
          return { 
            ...container, 
            children: updatedChildren,
          };
        } else {
          // For empty cells, create a placeholder child with backgroundColor only
          return {
            ...container,
            children: [
              ...container.children,
              {
                cellRow: row,
                cellColumn: col,
                backgroundColor: updatedCell.backgroundColor,
                element: {
                  id: `empty-${row}-${col}`,
                  type: 'text',
                  visible: false,
                  content: '',
                } as any,
              },
            ],
          };
        }
      }
      
      // Recursively update nested containers
      if (!container.children || container.children.length === 0) {
        return container;
      }
      
      const updatedChildren = container.children.map(child => {
        if (child.element.type === 'container') {
          return {
            ...child,
            element: updateContainerCell(child.element as ContainerElement),
          };
        }
        return child;
      });
      
      return { ...container, children: updatedChildren };
    };
    
    // Update the appropriate container
    // First check if container is in header (including nested)
    const isInHeader = (c: ContainerElement, targetId: string): boolean => {
      if (c.id === targetId) return true;
      if (!c.children) return false;
      return c.children.some(child => 
        child.element.type === 'container' && isInHeader(child.element as ContainerElement, targetId)
      );
    };
    
    const isInFooter = (c: ContainerElement, targetId: string): boolean => {
      if (c.id === targetId) return true;
      if (!c.children) return false;
      return c.children.some(child => 
        child.element.type === 'container' && isInFooter(child.element as ContainerElement, targetId)
      );
    };
    
    if (isInHeader(headerContainer, containerId)) {
      const updated = updateContainerCell(headerContainer);
      console.log('Updated headerContainer:', updated);
      setHeaderContainer(updated);
    } else if (isInFooter(footerContainer, containerId)) {
      const updated = updateContainerCell(footerContainer);
      console.log('Updated footerContainer:', updated);
      setFooterContainer(updated);
    } else if (rootContainer) {
      if (Array.isArray(rootContainer)) {
        const updated = rootContainer.map(c => updateContainerCell(c));
        console.log('Updated rootContainer array:', updated);
        setRootContainer(updated);
      } else {
        const updated = updateContainerCell(rootContainer);
        console.log('Updated rootContainer:', updated);
        setRootContainer(updated);
      }
    }
    
    setHasUnsavedChanges(true);
  };

  // Handle row background color change
  const handleRowBackgroundColorChange = (backgroundColor: string) => {
    if (!selectedRowInfo) return;
    
    const { containerId, row } = selectedRowInfo;
    
    const updateContainerRowColor = (
      container: ContainerElement
    ): { updatedContainer: ContainerElement; updated: boolean } => {
      if (container.id === containerId) {
        const rows = container.rows || 1;
        const cols = container.columns || 1;
        const currentColors = container.rowBackgroundColors || Array(rows).fill('');
        const updatedColors = [...currentColors];
        updatedColors[row] = backgroundColor;
        
        // Update or create children for all cells in the row
        const existingChildren = container.children.filter((c: any) => c.cellRow !== row);
        const rowChildren = [];
        
        for (let col = 0; col < cols; col++) {
          const existingChild = container.children.find((c: any) => c.cellRow === row && c.cellColumn === col);
          if (existingChild) {
            rowChildren.push({
              ...existingChild,
              backgroundColor,
            });
          } else {
            // Create placeholder for empty cell
            rowChildren.push({
              cellRow: row,
              cellColumn: col,
              backgroundColor,
              element: {
                id: `empty-${row}-${col}`,
                type: 'text',
                visible: false,
                content: '',
              } as any,
            });
          }
        }
        
        return {
          updatedContainer: {
            ...container,
            rowBackgroundColors: updatedColors,
            children: [...existingChildren, ...rowChildren],
          },
          updated: true,
        };
      }
      
      if (!container.children || container.children.length === 0) {
        return { updatedContainer: container, updated: false };
      }
      
      let childUpdated = false;
      const updatedChildren = container.children.map(child => {
        if (child.element.type !== 'container') {
          return child;
        }
        
        const { updatedContainer, updated } = updateContainerRowColor(child.element as ContainerElement);
        if (updated) {
          childUpdated = true;
          return {
            ...child,
            element: updatedContainer,
          };
        }
        return child;
      });
      
      if (childUpdated) {
        return {
          updatedContainer: {
            ...container,
            children: updatedChildren,
          },
          updated: true,
        };
      }
      
      return { updatedContainer: container, updated: false };
    };
    
    const tryUpdateContainer = (
      container: ContainerElement,
      setter: (c: ContainerElement) => void
    ): boolean => {
      const { updatedContainer, updated } = updateContainerRowColor(container);
      if (updated) {
        setter(updatedContainer);
      }
      return updated;
    };
    
    let didUpdate = false;
    if (!didUpdate && headerContainer) {
      didUpdate = tryUpdateContainer(headerContainer, setHeaderContainer);
    }
    if (!didUpdate && footerContainer) {
      didUpdate = tryUpdateContainer(footerContainer, setFooterContainer);
    }
    
    if (!didUpdate && rootContainer) {
      if (Array.isArray(rootContainer)) {
        const updatedContainers = rootContainer.map(container => {
          if (didUpdate) return container;
          const { updatedContainer, updated } = updateContainerRowColor(container);
          if (updated) {
            didUpdate = true;
            return updatedContainer;
          }
          return container;
        });
        if (didUpdate) {
          setRootContainer(updatedContainers);
        }
      } else {
        didUpdate = tryUpdateContainer(rootContainer, setRootContainer);
      }
    }
    
    if (didUpdate) {
      setHasUnsavedChanges(true);
    }
  };

  // Handle row height change
  const handleRowHeightChange = (rowHeight: string) => {
    if (!selectedRowInfo) return;
    
    const { containerId, row } = selectedRowInfo;
    
    const updateContainerRow = (container: ContainerElement): ContainerElement => {
      if (container.id === containerId) {
        const rows = container.rows || 1;
        const currentHeights = container.rowHeights || Array(rows).fill('1fr');
        const updatedHeights = [...currentHeights];
        updatedHeights[row] = rowHeight;
        
        return { 
          ...container, 
          rowHeights: updatedHeights,
        };
      }
      
      // Recursively update nested containers
      if (!container.children || container.children.length === 0) {
        return container;
      }
      
      const updatedChildren = container.children.map(child => {
        if (child.element.type === 'container') {
          return {
            ...child,
            element: updateContainerRow(child.element as ContainerElement),
          };
        }
        return child;
      });
      
      return { ...container, children: updatedChildren };
    };
    
    // Update the appropriate container
    if (headerContainer.id === containerId) {
      setHeaderContainer(updateContainerRow(headerContainer));
    } else if (footerContainer.id === containerId) {
      setFooterContainer(updateContainerRow(footerContainer));
    } else if (rootContainer) {
      if (Array.isArray(rootContainer)) {
        setRootContainer(rootContainer.map(c => updateContainerRow(c)));
      } else {
        setRootContainer(updateContainerRow(rootContainer));
      }
    }
    
    setHasUnsavedChanges(true);
  };

  // Load template data (only if not creating new template)
  useEffect(() => {
    if (isNewTemplate) {
      // Reset to defaults for new template
      setTemplateName('Neues Template')
      setGlobalFontFamily('Helvetica')
      setGlobalFontSize(10)
      setGlobalFontColor('#000000')
      setRootContainer([])
      setAbsoluteElements([])
      setHeaderContainer({
        id: 'header-container',
        type: 'container',
        visible: true,
        rows: 1,
        columns: 3,
        height: 100,
        width: 100,
        horizontalAlign: 'stretch',
        verticalAlign: 'stretch',
        children: [],
      })
      setFooterContainer({
        id: 'footer-container',
        type: 'container',
        visible: true,
        rows: 1,
        columns: 3,
        height: 80,
        width: 100,
        horizontalAlign: 'stretch',
        verticalAlign: 'stretch',
        children: [],
      })
      setHasUnsavedChanges(false)
      return
    }

    if (template) {
      setTemplateName(template.name || 'Neues Template')
      setGlobalFontFamily(template.fontFamily)
      setGlobalFontSize(template.fontSize)
      setGlobalFontColor(template.fontColor)

      // Load page structure
      if (template.rootContainer) {
        console.log('Loading page structure from template:', template.rootContainer);
        
        // Load page margins from rootContainer
        if (template.rootContainer.leftMargin !== undefined) setLeftMargin(template.rootContainer.leftMargin)
        if (template.rootContainer.rightMargin !== undefined) setRightMargin(template.rootContainer.rightMargin)
        if (template.rootContainer.topMargin !== undefined) setTopMargin(template.rootContainer.topMargin)
        if (template.rootContainer.bottomMargin !== undefined) setBottomMargin(template.rootContainer.bottomMargin)
        
        // Load header and footer if present, otherwise keep defaults
        if (template.rootContainer.header) {
          setHeaderContainer(template.rootContainer.header);
        }
        
        if (template.rootContainer.footer) {
          setFooterContainer(template.rootContainer.footer);
        }
        
        // Load containers
        if (template.rootContainer.containers) {
          setRootContainer(template.rootContainer.containers as ContainerElement | ContainerElement[]);
        } else {
          setRootContainer([]);
        }
        
        // Load absolute elements
        if (template.rootContainer.absoluteElements && template.rootContainer.absoluteElements.length > 0) {
          setAbsoluteElements(template.rootContainer.absoluteElements);
        } else {
          setAbsoluteElements([]);
        }
      } else {
        console.log('Template has no root container, using defaults');
        setRootContainer([]);
        setAbsoluteElements([]);
      }
      
      // Reset unsaved changes flag when template is loaded
      setHasUnsavedChanges(false)
    }
  }, [template, isNewTemplate])

  // Detect changes and mark as dirty
  useEffect(() => {
    if (!template) return;

    // Normalize empty values for comparison (null, undefined, [] are all "empty")
    const normalizeContainer = (container: ContainerElement | ContainerElement[] | null | undefined) => {
      if (!container) return null;
      if (Array.isArray(container) && container.length === 0) return null;
      return container;
    };

    const normalizeAbsoluteElements = (elements: AbsoluteElement[] | undefined) => {
      if (!elements || elements.length === 0) return null;
      return elements;
    };

    // Get current and template containers for comparison
    const currentContainers = normalizeContainer(rootContainer);
    const templateContainers = normalizeContainer(template.rootContainer?.containers);
    const currentAbsoluteElements = normalizeAbsoluteElements(absoluteElements);
    const templateAbsoluteElements = normalizeAbsoluteElements(template.rootContainer?.absoluteElements);

    const hasChanges = 
      globalFontFamily !== template.fontFamily ||
      globalFontSize !== template.fontSize ||
      globalFontColor !== template.fontColor ||
      leftMargin !== (template.rootContainer?.leftMargin ?? 56.7) ||
      rightMargin !== (template.rootContainer?.rightMargin ?? 56.7) ||
      topMargin !== (template.rootContainer?.topMargin ?? 56.7) ||
      bottomMargin !== (template.rootContainer?.bottomMargin ?? 56.7) ||
      JSON.stringify(currentContainers) !== JSON.stringify(templateContainers) ||
      JSON.stringify(currentAbsoluteElements) !== JSON.stringify(templateAbsoluteElements) ||
      JSON.stringify(headerContainer) !== JSON.stringify(template.rootContainer?.header) ||
      JSON.stringify(footerContainer) !== JSON.stringify(template.rootContainer?.footer);

    setHasUnsavedChanges(hasChanges);
  }, [template, globalFontFamily, globalFontSize, globalFontColor, leftMargin, rightMargin, topMargin, bottomMargin, rootContainer, absoluteElements, headerContainer, footerContainer])

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges])

  const handleBack = () => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm(
        t('invoiceTemplate.confirmLeave')
      );
      if (!confirmLeave) return;
    }
    navigate({ to: '/' });
  }

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg">{t('invoiceTemplate.loading')}</div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background" style={{ top: 0 }}>
      {/* Header */}
      <header className="flex items-center justify-between border-b px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBack}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-semibold">
            {t('invoiceTemplate.title')}
            {hasUnsavedChanges && <span className="text-orange-500 ml-2">{t('invoiceTemplate.unsavedChanges')}</span>}
          </h1>
          <div className="flex items-center gap-2">
            <Label htmlFor="template-name" className="text-sm">Name:</Label>
            <Input
              id="template-name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template Name"
              required
              className="w-64"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Global Settings */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon">
                <Settings className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h3 className="font-semibold">Globale Einstellungen</h3>
                <div>
                  <Label htmlFor="global-font-family">{t('invoiceTemplate.globalSettings.fontFamily')}</Label>
                  <Input
                    id="global-font-family"
                    value={globalFontFamily}
                    onChange={(e) => setGlobalFontFamily(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="global-font-size">{t('invoiceTemplate.globalSettings.fontSize')}</Label>
                  <Input
                    id="global-font-size"
                    type="number"
                    min={6}
                    max={72}
                    value={globalFontSize}
                    onChange={(e) => setGlobalFontSize(Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label htmlFor="global-font-color">{t('invoiceTemplate.globalSettings.fontColor')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="global-font-color"
                      type="color"
                      value={globalFontColor}
                      onChange={(e) => setGlobalFontColor(e.target.value)}
                      className="w-16 h-9 p-1"
                    />
                    <Input
                      type="text"
                      value={globalFontColor}
                      onChange={(e) => setGlobalFontColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Grid Toggle */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon">
                <Grid3x3 className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-grid">{t('invoiceTemplate.gridSettings.showGrid')}</Label>
                  <Switch
                    id="show-grid"
                    checked={showGrid}
                    onCheckedChange={setShowGrid}
                  />
                </div>
                {showGrid && (
                  <div>
                    <Label htmlFor="grid-size">{t('invoiceTemplate.gridSettings.gridSize')}</Label>
                    <Input
                      id="grid-size"
                      type="number"
                      value={gridSize}
                      onChange={(e) => setGridSize(Number(e.target.value))}
                      step="0.1"
                    />
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Import/Export */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!template}
            title="Template exportieren"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          
          <label>
            <input
              type="file"
              accept=".zip"
              onChange={handleImport}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              asChild
              title="Template importieren"
            >
              <span className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Import
              </span>
            </Button>
          </label>

          {/* Margins Toggle */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon">
                <Ruler className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-margins">{t('invoiceTemplate.marginSettings.showMargins')}</Label>
                  <Switch
                    id="show-margins"
                    checked={showMargins}
                    onCheckedChange={setShowMargins}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="envelope-window" className="text-xs">C4 Sichtfenster (DIN 5008)</Label>
                  <select
                    id="envelope-window"
                    value={showEnvelopeWindow}
                    onChange={(e) => setShowEnvelopeWindow(e.target.value as 'none' | 'form-a' | 'form-b')}
                    className="w-full h-8 text-xs border rounded px-2 bg-background text-foreground border-input"
                  >
                    <option value="none">Ausgeblendet</option>
                    <option value="form-a">Form A (27mm von oben)</option>
                    <option value="form-b">Form B (45mm von oben)</option>
                  </select>
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="show-rulers">{t('invoiceTemplate.marginSettings.showRulers')}</Label>
                  <Switch
                    id="show-rulers"
                    checked={showRulers}
                    onCheckedChange={setShowRulers}
                  />
                </div>
                {showMargins && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="left-margin">{t('invoiceTemplate.marginSettings.left')}</Label>
                      <Input
                        id="left-margin"
                        type="number"
                        value={(leftMargin / CM_TO_PX).toFixed(1)}
                        onChange={(e) =>
                          setLeftMargin(Number(e.target.value) * CM_TO_PX)
                        }
                        step="0.1"
                        min="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="right-margin">{t('invoiceTemplate.marginSettings.right')}</Label>
                      <Input
                        id="right-margin"
                        type="number"
                        value={(rightMargin / CM_TO_PX).toFixed(1)}
                        onChange={(e) =>
                          setRightMargin(Number(e.target.value) * CM_TO_PX)
                        }
                        step="0.1"
                        min="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="top-margin">{t('invoiceTemplate.marginSettings.top')}</Label>
                      <Input
                        id="top-margin"
                        type="number"
                        value={(topMargin / CM_TO_PX).toFixed(1)}
                        onChange={(e) =>
                          setTopMargin(Number(e.target.value) * CM_TO_PX)
                        }
                        step="0.1"
                        min="0"
                      />
                    </div>
                    <div>
                      <Label htmlFor="bottom-margin">{t('invoiceTemplate.marginSettings.bottom')}</Label>
                      <Input
                        id="bottom-margin"
                        type="number"
                        value={(bottomMargin / CM_TO_PX).toFixed(1)}
                        onChange={(e) =>
                          setBottomMargin(Number(e.target.value) * CM_TO_PX)
                        }
                        step="0.1"
                        min="0"
                      />
                    </div>
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>

          <Button 
            onClick={handleSave} 
            disabled={updateTemplate.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {t('invoiceTemplate.save')}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Property Panel */}
        <div className="w-80 border-r bg-background overflow-y-auto">
          <div className="p-4 space-y-6">
            {!selectedElement && !selectedCellInfo && !selectedRowInfo && (
              <div className="flex flex-col items-center justify-center h-full text-center space-y-4 py-12">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                  <svg 
                    className="w-8 h-8 text-muted-foreground" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" 
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold mb-2">{t('invoiceTemplate.noElementSelected')}</h3>
                  <p className="text-xs text-muted-foreground max-w-[200px]">
                    {t('invoiceTemplate.noElementSelectedDesc')}
                  </p>
                </div>
              </div>
            )}

            {/* Row Properties */}
            {selectedRowInfo && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold">
                    Zeilen-Eigenschaften
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedElementId(selectedRowInfo.containerId)}
                    className="h-7 text-xs"
                  >
                    ↑ Zum Container
                  </Button>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      Höhe
                    </label>
                    <select
                      value={selectedRowInfo.rowHeight}
                      onChange={(e) => handleRowHeightChange(e.target.value)}
                      className="w-full h-8 px-2 text-sm border rounded"
                    >
                      {rowHeightPresets.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                      {!rowHeightPresets.some((option) => option.value === selectedRowInfo.rowHeight) && (
                        <option value={selectedRowInfo.rowHeight}>
                          Benutzerdefiniert ({selectedRowInfo.rowHeight || '–'})
                        </option>
                      )}
                    </select>
                    <div className="mt-2 space-y-1">
                      <label className="text-xs font-medium text-muted-foreground block">
                        Benutzerdefinierte Höhe
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            type="number"
                            min={0}
                            step={1}
                            value={rowHeightInput}
                            onChange={(e) => handleRowHeightInputChange(e.target.value)}
                            placeholder="0"
                            className="h-8 text-sm pr-10"
                          />
                          <span className="absolute inset-y-0 right-3 flex items-center text-xs text-muted-foreground">
                            px
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1.5 block">
                      Hintergrundfarbe
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={selectedRowInfo.backgroundColor || '#ffffff'}
                        onChange={(e) => handleRowBackgroundColorChange(e.target.value)}
                        className="w-16 h-8 p-1 border rounded"
                      />
                      <input
                        type="text"
                        value={selectedRowInfo.backgroundColor || ''}
                        onChange={(e) => handleRowBackgroundColorChange(e.target.value)}
                        placeholder="transparent"
                        className="flex-1 h-8 px-2 text-sm border rounded"
                      />
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <p className="mb-1">Zeile {selectedRowInfo.row + 1}</p>
                    <p>Aktuell: <code className="bg-gray-100 px-1 rounded">{selectedRowInfo.rowHeight}</code></p>
                  </div>
                </div>
              </div>
            )}

            {/* Cell Properties */}
            {selectedCellInfo && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold">
                    Zellen-Eigenschaften
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedElementId(selectedCellInfo.containerId)}
                    className="h-7 text-xs"
                  >
                    ↑ Zum Container
                  </Button>
                </div>
                <GenericPropertyGrid
                  cell={selectedCellInfo.cellData}
                  onChange={(element) => handleCellChange(element as CellData)}
                />
              </div>
            )}

            {/* Container Properties */}
            {selectedContainer && selectedElement?.type === 'container' && (
              <div>
                <h2 className="font-semibold mb-3">
                  {selectedElement.id === headerContainer.id 
                    ? t('invoiceTemplate.properties.headerProperties')
                    : selectedElement.id === footerContainer.id
                    ? t('invoiceTemplate.properties.footerProperties')
                    : t('invoiceTemplate.properties.containerProperties')}
                </h2>
                <GenericPropertyGrid
                  element={selectedContainer}
                  onChange={(element) => updateHandlers.handleContainerChange(element as ContainerElement)}
                  isHeaderOrFooter={
                    selectedElement.id === headerContainer.id || 
                    selectedElement.id === footerContainer.id
                  }
                />
              </div>
            )}

            {/* Element Properties */}
            {selectedElement && selectedElement.type !== 'container' && (
              <div>
                <h2 className="font-semibold mb-3">
                  {t('invoiceTemplate.properties.elementProperties')}
                </h2>
                <GenericPropertyGrid
                  element={selectedElement}
                  onChange={(element) => updateHandlers.handleElementChange(element as TemplateElement | AbsoluteElement)}
                  globalFontFamily={globalFontFamily}
                  globalFontSize={globalFontSize}
                  globalFontColor={globalFontColor}
                  onBringToFront={selectedElement.type === 'absolute-image' ? () => absoluteHandlers.handleBringToFront(selectedElement.id) : undefined}
                  onSendToBack={selectedElement.type === 'absolute-image' ? () => absoluteHandlers.handleSendToBack(selectedElement.id) : undefined}
                  onBringForward={selectedElement.type === 'absolute-image' ? () => absoluteHandlers.handleBringForward(selectedElement.id) : undefined}
                  onSendBackward={selectedElement.type === 'absolute-image' ? () => absoluteHandlers.handleSendBackward(selectedElement.id) : undefined}
                  maxHeight={getParentContainerMaxHeight(selectedElement.id, headerContainer, footerContainer, rootContainer, topMargin, bottomMargin)}
                />
              </div>
            )}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 overflow-auto bg-muted p-8">
          <GridCanvas
            documentRef={canvasRef}
            rootContainer={rootContainer}
            onChange={(newContainer) => {
              console.log('GridCanvas onChange called with:', newContainer);
              setRootContainer(newContainer);
            }}
              selectedElementId={selectedElementId}
              onSelectElement={setSelectedElementId}
              absoluteElements={absoluteElements}
              onAbsoluteElementsChange={setAbsoluteElements}
              onCutAbsoluteElement={absoluteHandlers.handleCutAbsoluteElement}
              onCopyAbsoluteElement={absoluteHandlers.handleCopyAbsoluteElement}
              onPasteAbsoluteElement={absoluteHandlers.handlePasteAbsoluteElement}
              onDuplicateAbsoluteElement={absoluteHandlers.handleDuplicateAbsoluteElement}
              onBringToFront={absoluteHandlers.handleBringToFront}
              onSendToBack={absoluteHandlers.handleSendToBack}
              onBringForward={absoluteHandlers.handleBringForward}
              onSendBackward={absoluteHandlers.handleSendBackward}
              hasClipboard={clipboard !== null}
              fontFamily={globalFontFamily}
              fontSize={globalFontSize}
              fontColor={globalFontColor}
              showGrid={showGrid}
              gridSize={gridSize}
              showRulers={showRulers}
              leftMargin={leftMargin}
              rightMargin={rightMargin}
              topMargin={topMargin}
              bottomMargin={bottomMargin}
              showMargins={showMargins}
              showEnvelopeWindow={showEnvelopeWindow}
              headerContainer={headerContainer}
              onHeaderChange={setHeaderContainer}
              footerContainer={footerContainer}
              onFooterChange={setFooterContainer}
            />
        </div>

        {/* Outline View */}
        <div className="w-80">
          <OutlineView
            headerContainer={headerContainer}
            footerContainer={footerContainer}
            rootContainer={rootContainer || []}
            absoluteElements={absoluteElements}
            selectedElementId={selectedElementId}
            onSelectElement={setSelectedElementId}
            onDeleteElement={handleDeleteElement}
            onDeleteRow={handleDeleteRow}
          />
        </div>
      </div>
    </div>
  )
}
